import React from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { SvgIcons } from "@tripian/react";
import Model from "@tripian/model";
import {
    CITY_INFO,
    UPDATE_TRIP,
} from "../../../../constants/ROUTER_PATH_TITLE";
import TabMenu from "../../../TripPlan/components/Itineraries/Tabs/Tabs";
import { useSelector } from "react-redux";
import ICombinedState from "../../../../redux/model/ICombinedState";
import { useIsMobileForTripLayout } from "../../../../hooks/useIsMobileForTripLayout";
import { useGuestUserForSimplyCaribbean } from "../../../../hooks/useGuestUserForSimplyCaribbean";

import classes from "./TripLayout.module.scss";

interface TimelineTripLayoutProps {
    tripReference: Model.TripReference;
    selectedTabIndex: number;
    isFocusOpen: boolean;
    setSelectedTabIndex: (index: number) => void;
    setShowCloneModal: () => void;
    setShowShareTripModal: () => void;
    onFocusLost: () => void;
    t: (value: Model.TranslationKey) => string;
    children: React.ReactNode;
    editPath?: string; // Custom edit path for timeline trips
}

const TimelineTripLayout: React.FC<TimelineTripLayoutProps> = ({
                                                   tripReference,
                                                   selectedTabIndex,
                                                   isFocusOpen,
                                                   setSelectedTabIndex,
                                                   setShowCloneModal,
                                                   setShowShareTripModal,
                                                   onFocusLost,
                                                   t,
                                                   children,
                                                   editPath,
                                               }) => {
    const navigate = useNavigate();
    const sharedTrip = tripReference.shared;

    const { ref: TimelineTripLayoutRef, isMobile } = useIsMobileForTripLayout();

    const { readOnlyTrip } = useSelector((state: ICombinedState) => ({
        readOnlyTrip: state.trip.readOnly,
    }));

    const isGuestForSimplyCaribbean = useGuestUserForSimplyCaribbean();

    return (
        <div
            className={`${classes.TimelineTripLayout} container-height ${isFocusOpen ? classes.disabled : ""}`}
            ref={TimelineTripLayoutRef}
        >
            {isFocusOpen && (
                <div
                    className={classes.TimelineTripLayoutOverlay}
                    onClick={onFocusLost}
                    aria-label="Close focus panel"
                />
            )}
            <div className="bg-background-color mt-10 px-8 w-full">
                <div className="flex justify-between items-start mb-8">
                    <div>
                        <div className="flex items-start gap-2 mb-6">
              <span className="text-3xl/[1.875rem] font-medium max-w-[10rem] lg:max-w-full">
                {tripReference.tripProfile.tripName || tripReference.city.name}
              </span>
                            <div
                                className="cursor-pointer mt-1"
                                onClick={() =>
                                    navigate(`${CITY_INFO.PATH}/${tripReference.city.id}`)
                                }
                                title="Click for more info"
                            >
                                <SvgIcons.Info3 />
                            </div>
                        </div>
                        {tripReference &&
                            !(sharedTrip && window.tconfig.WIDGET_THEME_1) && (
                                <div className="text-base font-normal border border-neutral-300 rounded-full px-6 py-[0.4375rem] mt-2 min-w-52">
                                    {moment(tripReference.tripProfile.arrivalDatetime).format(
                                        "MMM DD"
                                    )}{" "}
                                    –{" "}
                                    {moment(tripReference.tripProfile.departureDatetime).format(
                                        "MMM DD YYYY"
                                    )}
                                </div>
                            )}
                    </div>

                    {!(sharedTrip && window.tconfig.WIDGET_THEME_1) && (
                        <div className="flex gap-4">
                            {readOnlyTrip || isGuestForSimplyCaribbean ? (
                                <div>
                                    <div
                                        className="border border-solid border-neutral-300 rounded-full w-10 h-10 flex justify-center items-center cursor-pointer"
                                        onClick={() => setShowCloneModal()}
                                    >
                                        <SvgIcons.Bookmark size="1.25rem" />
                                    </div>
                                    {window.tconfig.GUEST_LOGIN && (
                                        <div className="text-sm text-center font-medium text-gray-600 max-w-[3.5rem] mt-1">
                                            {t(
                                                "trips.myTrips.itinerary.customPoiModal.locationModal.addLocation.address.submit"
                                            )}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <>
                                    {window.tconfig.SHARED_TRIP && (
                                        <>
                                            {window.tconfig.GUEST_LOGIN ? (
                                                <div>
                                                    <div
                                                        className="border border-solid border-neutral-300 rounded-full w-10 h-10 flex justify-center items-center cursor-pointer text-sm text-gray-600"
                                                        onClick={() => setShowShareTripModal()}
                                                    >
                                                        <SvgIcons.MobileArrowRight
                                                            width="1.25rem"
                                                            height="1.25rem"
                                                        />
                                                    </div>
                                                    <div className="text-sm font-medium text-gray-600 max-w-[3.5rem] mt-1">
                                                        {t("switchToMobile")}
                                                    </div>
                                                </div>
                                            ) : (
                                                <div
                                                    className="border border-solid border-neutral-300 rounded-full w-10 h-10 flex justify-center items-center cursor-pointer"
                                                    onClick={() => setShowShareTripModal()}
                                                >
                                                    <SvgIcons.Share
                                                        size="1.125rem"
                                                        className="primary-color"
                                                    />{" "}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>

                <TabMenu
                    menuItems={[
                        {
                            title: t("itinerary" as Model.TranslationKey),
                            onClick: () => {
                                setSelectedTabIndex(0);
                            },
                        },
                        {
                            title: isMobile ? t("bookable.mobile") : t("bookable.desktop"),
                            onClick: () => {
                                setSelectedTabIndex(1);
                            },
                        },
                        {
                            title: isMobile ? t("explore.mobile") : t("explore.desktop"),
                            onClick: () => {
                                setSelectedTabIndex(2);
                            },
                        },

                        // ...(window.tconfig.BRAND_NAME === "Visa"
                        //   ? [
                        //       {
                        //         title: isMobile ? t("visa.hotel.mobile") : t("visa.hotel.desktop"),
                        //         onClick: () => setSelectedTabIndex(3),
                        //       },
                        //     ]
                        //   : []),
                    ]}
                    selectedIndex={selectedTabIndex}
                />
                {children}
            </div>
        </div>
    );
};

export default TimelineTripLayout;
